import { useEffect, useState } from "react";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

function renderMarkdown(text) {
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      out.push(<ul key={`ul-${key}`} className="space-y-1.5 my-2 ml-1">{list}</ul>);
      list = [];
    }
  };
  const inline = (s) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>
        : <span key={i}>{part}</span>
    );

  lines.forEach((line, i) => {
    const t = line.trim();
    if (t.startsWith("## ")) {
      flush(i);
      out.push(<h4 key={i} className="font-display font-semibold text-sm uppercase tracking-wider text-primary mt-4 mb-1 first:mt-0">{t.slice(3)}</h4>);
    } else if (t.startsWith("# ")) {
      flush(i);
      out.push(<h3 key={i} className="font-display font-bold text-base mt-3 mb-1">{t.slice(2)}</h3>);
    } else if (t.startsWith("- ") || t.startsWith("* ")) {
      list.push(
        <li key={i} className="text-sm text-muted-foreground flex gap-2">
          <span className="text-primary mt-0.5">▸</span><span>{inline(t.slice(2))}</span>
        </li>
      );
    } else if (t) {
      flush(i);
      out.push(<p key={i} className="text-sm text-muted-foreground my-2">{inline(t)}</p>);
    }
  });
  flush("end");
  return out;
}

export default function AiInsights({ dsId }) {
  const [content, setContent] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/datasources/${dsId}/insights`);
        setContent(data.content);
        setGeneratedAt(data.generated_at);
      } catch { /* ignore */ }
    })();
  }, [dsId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/datasources/${dsId}/insights`);
      setContent(data.content);
      setGeneratedAt(data.generated_at);
      toast.success("Insight AI dihasilkan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl bg-card p-4 h-full flex flex-col shadow-sm ring-1 ring-black/[0.03]" data-testid="ai-insights-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold text-base flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-primary" /> Analisa AI
        </h3>
        <Button size="sm" onClick={generate} disabled={loading} className="h-8 rounded-xl text-xs gap-1.5"
          data-testid="generate-insights-button">
          {loading ? <ArrowClockwise size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
          {content ? "Perbarui" : "Hasilkan"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Claude Sonnet 4.6</p>

      <div className="flex-1 overflow-y-auto mt-3 pr-1">
        {loading ? (
          <div className="space-y-2 mt-4">
            <div className="h-3 bg-secondary animate-pulse w-3/4" />
            <div className="h-3 bg-secondary animate-pulse w-full" />
            <div className="h-3 bg-secondary animate-pulse w-5/6" />
            <p className="text-xs text-muted-foreground pt-4">Claude sedang menganalisa data Anda…</p>
          </div>
        ) : content ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{renderMarkdown(content)}</motion.div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center py-8">
            <Sparkle size={32} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Dapatkan temuan, tren, dan rekomendasi otomatis dari data Anda.</p>
          </div>
        )}
      </div>
    </div>
  );
}
